import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  getSegmentImages,
  getTripDriver,
  getTripTracking,
  getVehicleGpsLocation,
  getTripSecurityGuard,
  getLegCrews,
  getHotelCrews,
  getHotelTransportCrews,
  getAllocateHotelList,
  getStationAddress,
} from "../../apis/creweasyMobileApi";
import TransportDetail from "./CrewActivityTrackMap";

/**
 * CrewActivityMobileViewComponent
 * Web view of the crew activity dashboard (crew mobile app visual language).
 *
 * Dashboard: day groups -> one full-width card per segment, colour by
 * isNonFlyActivity (peach when true, white when false — a day can mix both),
 * pink + CANCELLED stamp when isTransportCancelled / isHotelCancelled.
 * Clicking a card expands its detail panel directly below it.
 *
 * Detail APIs (called on expand, skeleton until each resolves):
 *   local-vendor-vehicle-image  (Transport images)
 *   hotel-shuttle-image         (Transport ( Hotel ) images)
 *   hotel-image                 (Hotel Stay images)
 *   hotel-crews                 (Hotel Stay crew members)
 *   hotel-amenities             (Hotel Stay amenities)
 *   leg-crews                   (Flight crew members)
 *   Crew/get-crew-dropdown-active      (Staff select box in the header)
 *   get-allocate-hotel-list     (Change Hotel dropdown)
 *   get-early-pickup-time       (Early Pickup options)
 *   station-address             (Change pick-up / drop-off address)
 *   get-trip-driver             (driverInfo + crews + eta)
 *   get-trip-tracking           (driverInfo + passengers + destination + route + eta — Track button map;
 *                                route.mapJson/wayPoints draws the real road route via tripRoute.js, and
 *                                eta drives the single current-leg ETA/ETD message + progress dial)
 *   get-vehicle-gps-location    (latitude/longitude/speed/bearing/altitude/gpsTime — polled every 10s while
 *                                the Track map is open, to move/rotate the live vehicle marker)
 *   get-trip-security-guard     (securityGuardInfo)
 *   hotel-transport-crews       (crews for Transport ( Hotel ))
 *
 * Props
 *  - dashboardData, employeeNumber, airlineCode, recordCount
 *  - isLoading, onRefresh, onBack
 *  - fetchSegmentImages(activity)  -> Promise<string[]>
 *  - fetchTripDriver(activity, employeeNumber)     -> Promise<{ driverInfo, crews, eta }>
 *  - fetchTripTracking(activity, employeeNumber)   -> Promise<{ driverInfo, passengers, destination, route, eta }>
 *  - fetchVehicleGpsLocation(activity, employeeNumber) -> Promise<{ latitude, longitude, speed, bearing, altitude, gpsTime, isReloadMap, isGetEta }>
 *      isGetEta is intentionally unread — no separate ETA endpoint is called; ETA is derived
 *      locally from get-trip-tracking's own eta object, refreshed only via isReloadMap or the
 *      client-detected off-route replot (see LiveTripTracking.jsx for the identical pattern).
 *  - fetchTripSecurityGuard(activity, employeeNumber) -> Promise<securityGuardInfo>
 *  - fetchHotelTransportCrews(activity) -> Promise<crews[]>
 *  - onCancelRide, onReadyForPickup, onEarlyPickup,
 *    onChangePickupAddress, onChangeDropoffAddress, onTrackVehicle
 */
export const SEGMENT_ACTION_NON_CLICKABLE = true;
export const PURPLE = "#5B3FD6";
export const PURPLE_DARK = "#4834A6";
export const ORANGE = "#F26522";
export const RED = "#E01B24";
export const GREY = "#8a8a92";
const CARD_NONFLY = "#FBDFC8";
const CARD_CANCELLED = "#FCE4E4";
const CARD_LIVE_TRACKING = "#DCFCE7";
export const GREEN_RADIANT = "#22C55E";
const GREEN_DARK = "#0F5132";
// Whole-route congestion coloring for the Track map's drawn route — mirrors
// LiveTripTracking.jsx's TRAFFIC_COLORS/trafficLevel, reusing this file's own palette
// (ORANGE/RED already defined above) instead of introducing new colors.
export const TRAFFIC_COLORS = { light: GREEN_RADIANT, moderate: ORANGE, heavy: RED };
export const trafficLevel = (ratio) => (ratio >= 1.8 ? "heavy" : ratio >= 1.3 ? "moderate" : "light");

const WEATHER_ICON = {
  clear: "fas fa-sun",
  mainlyClear: "fas fa-cloud-sun",
  partlyCloudy: "fas fa-cloud-sun",
  cloudy: "fas fa-cloud",
  overcast: "fas fa-cloud",
  drizzle: "fas fa-cloud-rain",
  rain: "fas fa-cloud-showers-heavy",
  thunder: "fas fa-bolt",
  fog: "fas fa-smog",
  snow: "fas fa-snowflake",
  other: "fas fa-cloud-sun",
};
const WEATHER_COLOR = {
  clear: "#F5A524",
  mainlyClear: "#F5A524",
  partlyCloudy: "#F5A524",
  cloudy: "#8a8a92",
  overcast: "#8a8a92",
  drizzle: "#4A9EE8",
  rain: "#2E7BC4",
  thunder: "#F5A524",
  fog: "#8a8a92",
  snow: "#4A9EE8",
  other: "#8a8a92",
};

const AMENITY_ICON = {
  wifi: "fas fa-wifi",
  restaurant: "fas fa-utensils",
  gym: "fas fa-dumbbell",
  pool: "fas fa-swimmer",
  laundry: "fas fa-tshirt",
  parking: "fas fa-parking",
  ac: "fas fa-snowflake",
  roomService: "fas fa-concierge-bell",
  spa: "fas fa-spa",
  shuttle: "fas fa-shuttle-van",
  safe: "fas fa-lock",
  tv: "fas fa-tv",
  blackout: "fas fa-moon",
  desk: "fas fa-clock",
  other: "fas fa-check",
};

export const KIND_ICON = {
  flight: "fas fa-plane",
  hotel: "fas fa-bed",
  hotelTransport: "fas fa-car",
  transport: "fas fa-car",
  home: "fas fa-home",
  airportDuty: "fas fa-clipboard-list",
  groundDuty: "fas fa-briefcase",
  other: "fas fa-calendar-day",
};

/**
 * Detail objects are read case-insensitively: some environments serialize the
 * CreweasyMobile models PascalCase (EstimatedPickupTime), others camelCase.
 * camelKeys() normalizes so the views can always read camelCase.
 */
/** Bump when this file changes; logged once on mount so you can confirm the running build. */
export const CREW_ACTIVITY_VIEW_VERSION = "2026-08-25.12";

/** Trims API strings; whitespace-only values ("" or " ") become null so rows hide instead of rendering blank. */
/** A coordinate pair is usable only when both values are real, non-zero and in range. */
export const hasGeo = (lat, lng) => {
  const a = Number(lat);
  const b = Number(lng);
  return (
    Number.isFinite(a) &&
    Number.isFinite(b) &&
    a !== 0 &&
    b !== 0 &&
    Math.abs(a) <= 90 &&
    Math.abs(b) <= 180
  );
};

/** Shown in place of a Leaflet map when the payload has no usable coordinates. */
const NoGeo = ({ height }) => (
  <div
    style={{
      position: "relative",
      height,
      borderRadius: 10,
      overflow: "hidden",
      background: "#E8EAE6",
      border: "1px solid #e2e0ea",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 7,
      color: GREY,
    }}
  >
    <i
      className="fas fa-map-marked-alt"
      style={{ fontSize: 26, color: "#b8b6c4" }}
    />
    <span style={{ fontSize: 12.5, fontWeight: 700 }}>
      Geo location not available
    </span>
  </div>
);


export const escapeHtml = (s) =>
  String(s ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c],
  );

/** Circular avatar: the photo when `url` is truthy, otherwise a colored initials badge (via the shared `initialsOf`, defined below). Used in map popups (raw HTML, not React). */
export const avatarHtml = (url, name, size) =>
  url
    ? '<img src="' +
      escapeHtml(url) +
      '" style="width:' +
      size +
      "px;height:" +
      size +
      'px;border-radius:50%;object-fit:cover;flex:0 0 auto;" />'
    : '<span style="width:' +
      size +
      "px;height:" +
      size +
      "px;border-radius:50%;background:#EDE8FD;color:" +
      PURPLE +
      ";display:inline-flex;align-items:center;justify-content:center;font:800 " +
      Math.round(size * 0.38) +
      'px/1 Nunito,sans-serif;flex:0 0 auto;">' +
      escapeHtml(initialsOf(name)) +
      "</span>";

/** POI pin (public/assets/img/poi/red.png) with a title/subtitle label underneath — shared by every segment map. */
const poiMarkerIcon = (L, title, subtitle) =>
  L.divIcon({
    className: "",
    iconSize: null,
    iconAnchor: [16, 34],
    html:
      '<div style="display:flex;flex-direction:column;align-items:center;width:max-content;max-width:220px;">' +
      '<img src="/assets/img/poi/red.png" style="width:32px;height:32px;" />' +
      '<div style="margin-top:2px;background:rgba(255,255,255,0.94);border-radius:6px;padding:3px 8px;' +
      'text-align:center;font:800 10.5px/1.3 Nunito,sans-serif;color:#1c1c1e;box-shadow:0 2px 6px rgba(40,30,70,0.18);">' +
      (title ? `<div>${escapeHtml(title)}</div>` : "") +
      (subtitle
        ? `<div style="font-weight:600;color:#6a6a75;">${escapeHtml(subtitle)}</div>`
        : "") +
      "</div></div>",
  });

export const clean = (v) => {
  if (v === null || v === undefined) return null;
  if (typeof v === "string") {
    const t = v.trim();
    return t.length ? t : null;
  }
  if (typeof v === "number" || typeof v === "boolean") return v;
  // Some serializers emit [] (or {}) where a string was expected: both are
  // truthy but render as nothing, so normalize them to null / joined text.
  if (Array.isArray(v)) {
    const parts = v
      .map((x) => clean(x))
      .filter((x) => x !== null && x !== undefined);
    return parts.length ? parts.join(", ") : null;
  }
  if (typeof v === "object") return null;
  return v;
};

/** Visible placeholder when a value is missing but the row is always shown. */
export const show = (v) => clean(v) ?? "--";

export const camelKeys = (obj) => {
  if (!obj || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map((item) => camelKeys(item));
  const out = {};
  Object.keys(obj).forEach((k) => {
    const v = obj[k];
    out[k.charAt(0).toLowerCase() + k.slice(1)] =
      typeof v === "string" ? clean(v) : camelKeys(v);
  });
  return out;
};

export const kindOf = (raw) => {
  const a = camelKeys(raw || {});
  if (a.showFlight) return "flight";
  if (a.showHotel) return "hotel";
  if (a.showHotelTransport) return "hotelTransport";
  if (a.showLocalTransport) return "transport";
  if (a.showHome) return "home";
  if (a.showAirportDuty) return "airportDuty";
  if (a.showGroundDuty) return "groundDuty";
  return "other";
};

const isTransportKind = (kind) =>
  kind === "transport" || kind === "hotelTransport";
const segmentCancelled = (a) => {
  const kind = kindOf(a);
  if (isTransportKind(kind) || kind === "home")
    return a.isTransportCancelled === true;
  if (kind === "hotel") return a.isHotelCancelled === true;
  return false;
};

const cardBackground = (a) =>
  segmentCancelled(a)
    ? CARD_CANCELLED
    : a.isNonFlyActivity
      ? CARD_NONFLY
      : "#ffffff";
const dashColor = (a) =>
  segmentCancelled(a) ? "#dcbcbc" : a.isNonFlyActivity ? "#cbb49f" : "#d3d3da";

// No profile photo -> first letter of each word, max two letters
const initialsOf = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join("");

/** Crew-row tripStatus pill — colored off the explicit flags, not the status text itself. */
export const crewStatusStyle = (isBoarded, isCompleted, isCancelled) => {
  if (isCancelled)
    return { bg: "#F8D8D8", fg: "#8B1A1A", icon: "fas fa-times-circle" };
  if (isCompleted || isBoarded)
    return { bg: "#D0EED9", fg: "#1e7a45", icon: "fas fa-check-circle" };
  return { bg: "#EAEAEF", fg: "#6a6a75", icon: "far fa-clock" };
};

/** Security Guard OTP-used pill. */
export const verifiedStyle = (used) =>
  used
    ? {
        bg: "#D0EED9",
        fg: "#1e7a45",
        icon: "fas fa-check-circle",
        label: "Verified",
      }
    : {
        bg: "#F8D8D8",
        fg: RED,
        icon: "fas fa-times-circle",
        label: "Not Verified",
      };

export const S = {
  page: {
    background: "#F2F2F7",
    minHeight: "100%",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    position: "sticky",
    top: 0,
    zIndex: 1000,
    background: ORANGE,
    padding: "10px 16px",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "12px 18px",
    boxShadow: "0 2px 12px rgba(40,30,70,0.16)",
  },
  headerRule: { width: 1, height: 34, background: "rgba(255,255,255,0.35)" },
  iconButton: {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: "rgba(255,255,255,0.24)",
    color: "#fff",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  backLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 32,
    padding: "0 12px",
    borderRadius: 8,
    background: "rgba(255,255,255,0.24)",
    border: "none",
    fontFamily: "inherit",
    fontSize: 12.5,
    fontWeight: 700,
    color: "#fff",
    cursor: "pointer",
  },
  dayStrip: {
    position: "sticky",
    top: "var(--ca-hdr-h, 68px)",
    zIndex: 800,
    boxShadow: "0 1px 8px rgba(40,30,70,0.10)",
    background: "#DDD6FE",
    height: 52,
    display: "flex",
    alignItems: "center",
    gap: 14,
    paddingRight: 16,
  },
  dayChip: {
    width: 60,
    background: PURPLE,
    color: "#fff",
    borderRadius: "0 10px 10px 0",
    alignSelf: "stretch",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1.05,
  },
  dayCount: {
    marginLeft: "auto",
    display: "inline-flex",
    alignItems: "center",
    height: 26,
    padding: "0 11px",
    borderRadius: 999,
    background: PURPLE,
    color: "#fff",
    fontSize: 11.5,
    fontWeight: 800,
  },
  list: {
    padding: "8px 10px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  chevron: {
    position: "absolute",
    right: 4,
    top: "50%",
    transform: "translateY(-50%)",
    width: 24,
    height: 24,
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: PURPLE,
    pointerEvents: "none",
  },
  stamp: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
  },
  stampText: {
    transform: "rotate(-12deg)",
    fontSize: 30,
    fontWeight: 800,
    letterSpacing: "0.05em",
    color: "#8B1A1A",
    opacity: 0.8,
  },
  panel: {
    // one recessed lavender surface for every segment type; the card colour
    // still carries fly / non-fly, and the inner cards stay white
    background: "#EDEAF7",
    borderTop: "1px solid #DCD6F0",
    borderRadius: 12,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    padding: "12px 14px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  infoCard: {
    background: "#fff",
    border: "1px solid #f0eff5",
    borderRadius: 10,
    padding: "10px 12px",
    display: "flex",
    gap: 10,
    alignItems: "center",
  },
  iconCircle: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    background: "#FDEBDF",
    color: ORANGE,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  label: { fontSize: 13, color: GREY, fontWeight: 600 },
  value: {
    fontSize: 12.5,
    color: "#1c1c1e",
    fontWeight: 700,
    lineHeight: 1.45,
  },
  sectionTitle: { fontSize: 14, fontWeight: 800, color: "#1c1c1e" },
  note: {
    display: "flex",
    gap: 9,
    alignItems: "flex-start",
    background: "#E6DFFB",
    borderRadius: 10,
    padding: "11px 12px",
  },
  skeletonBar: { display: "block", width: "100%", borderRadius: 10, background: "#d9d9e3" },
};

const Dot = () => <span style={{ color: "#b8b8c0" }}>&middot;</span>;

const MetaPair = ({ iconClass, iconColor, children }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      whiteSpace: "nowrap",
    }}
  >
    <i className={iconClass} style={{ color: iconColor, fontSize: 11 }} />
    {children}
  </span>
);

export const Skeleton = ({ height, width, style }) => (
  <span
    style={{
      ...S.skeletonBar,
      height,
      ...(width ? { width } : null),
      animation: "skpulse 1.2s ease-in-out infinite",
      ...style,
    }}
  />
);

export const Avatar = ({ url, name, size = 38 }) => (
  <span
    style={{
      width: size,
      height: size,
      borderRadius: "50%",
      overflow: "hidden",
      background: "#EDE8FD",
      color: PURPLE,
      flexShrink: 0,
      position: "relative",
    }}
  >
    {url ? (
      <img
        src={url}
        alt={name}
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
    ) : (
      <span
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: size <= 38 ? 13 : 15,
          fontWeight: 800,
        }}
      >
        {initialsOf(name)}
      </span>
    )}
  </span>
);

export const ImageSlider = ({ images, loading, kind }) => {
  const [index, setIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const count = (images || []).length;
  const step = (delta) =>
    setIndex((i) => (count ? (i + delta + count) % count : 0));
  const title =
    kind === "hotel"
      ? "Hotel Photos"
      : kind === "hotelTransport"
        ? "Shuttle Photos"
        : "Vehicle Photos";

  return (
    <>
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          cursor: "pointer",
        }}
      >
        <span style={S.sectionTitle}>{title}</span>
        {!loading && count > 0 && (
          <span style={{ fontSize: 11.5, fontWeight: 700, color: GREY }}>
            {count}
          </span>
        )}
        <span
          style={{
            marginLeft: "auto",
            width: 24,
            height: 24,
            borderRadius: 6,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: PURPLE,
          }}
        >
          <i
            className={open ? "fas fa-chevron-down" : "fas fa-chevron-right"}
            style={{ fontSize: 11 }}
          />
        </span>
      </div>
      {!open ? null : (
        <div
          style={{
            position: "relative",
            aspectRatio: "2 / 1",
            maxHeight: 320,
            borderRadius: 10,
            overflow: "hidden",
            background: "#23202e",
          }}
        >
          {loading ? (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                background: "#2b2838",
                animation: "skpulse 1.2s ease-in-out infinite",
              }}
            >
              <span
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  background: "#3b3750",
                }}
              />
              <span
                style={{
                  width: 180,
                  height: 10,
                  borderRadius: 5,
                  background: "#3b3750",
                }}
              />
            </div>
          ) : (
            <>
              {count > 0 && images[index] ? (
                <img
                  src={images[index]}
                  alt={
                    kind === "hotel"
                      ? "Hotel"
                      : kind === "hotelTransport"
                        ? "Shuttle"
                        : "Vehicle"
                  }
                  style={{
                    position: "absolute",
                    inset: 0,
                    margin: "auto",
                    maxWidth: "100%",
                    maxHeight: "100%",
                    width: "auto",
                    height: "auto",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              ) : (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    color: "#8f8b9e",
                  }}
                >
                  <i
                    className={
                      kind === "hotel" ? "fas fa-bed" : "fas fa-car-side"
                    }
                    style={{ fontSize: 34 }}
                  />
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                    }}
                  >
                    No photo
                  </span>
                </div>
              )}

              {count > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 12,
                    background: "rgba(60,60,67,0.55)",
                    color: "#fff",
                    borderRadius: 999,
                    padding: "3px 10px",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {index + 1}/{count}
                </div>
              )}

              {count > 1 && (
                <>
                  <div
                    style={{
                      position: "absolute",
                      bottom: 10,
                      left: 0,
                      right: 0,
                      display: "flex",
                      justifyContent: "center",
                      gap: 5,
                    }}
                  >
                    {images.map((u, i) => (
                      <span
                        key={u + i}
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          background: i === index ? RED : "#cfc7e6",
                        }}
                      />
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => step(-1)}
                    style={sliderArrow("left")}
                  >
                    <i
                      className="fas fa-chevron-left"
                      style={{ fontSize: 11 }}
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => step(1)}
                    style={sliderArrow("right")}
                  >
                    <i
                      className="fas fa-chevron-right"
                      style={{ fontSize: 11 }}
                    />
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

const sliderArrow = (side) => ({
  position: "absolute",
  top: "50%",
  [side]: 8,
  transform: "translateY(-50%)",
  width: 28,
  height: 28,
  borderRadius: "50%",
  background: "rgba(255,255,255,0.85)",
  color: "#3c3c43",
  border: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
});

S.transporterBar = {
  background: ORANGE,
  borderRadius: 8,
  minHeight: 34,
  display: "flex",
  alignItems: "center",
  gap: 9,
  padding: "5px 11px",
};
S.transporterIcon = {
  width: 24,
  height: 24,
  borderRadius: "50%",
  background: "rgba(255,255,255,0.28)",
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};
S.formCard = {
  background: "#fff",
  border: "1px solid #e6dffb",
  borderRadius: 10,
  padding: 12,
  display: "flex",
  flexDirection: "column",
  gap: 10,
};
S.formHead = { display: "flex", alignItems: "center", gap: 8 };
S.formHeadIcon = {
  width: 26,
  height: 26,
  borderRadius: 7,
  background: "rgba(91,63,214,0.10)",
  color: PURPLE,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
S.formHeadText = { fontSize: 13.5, fontWeight: 800, color: "#1c1c1e" };
S.formLabel = { fontSize: 11.5, fontWeight: 700, color: GREY };
S.select = {
  height: 38,
  borderRadius: 8,
  border: "1px solid #dcd6ef",
  background: "#fff",
  color: "#1c1c1e",
  fontSize: 13,
  fontWeight: 700,
  padding: "0 10px",
  fontFamily: "inherit",
};

export const InfoTile = ({ icon, iconBg, iconFg, label, value, valueStyle }) => (
  <div style={S.infoCard}>
    <span
      style={{
        ...S.iconCircle,
        background: iconBg || "#FDEBDF",
        color: iconFg || ORANGE,
      }}
    >
      <i className={icon} style={{ fontSize: 13 }} />
    </span>
    <div style={{ minWidth: 0 }}>
      <div style={S.label}>{label}</div>
      <div style={{ ...S.value, ...valueStyle }}>{value}</div>
    </div>
  </div>
);


const HotelStayDetail = ({
  activity,
  images,
  imagesLoading,
  crews,
  crewsLoading,
  amenities,
  amenitiesLoading,
  handlers = {},
}) => {
  const d = camelKeys(activity.hotelDetails || {});
  const live = activity.isHotelCancelled !== true;
  const [crewOpen, setCrewOpen] = useState(false);
  const [amenitiesOpen, setAmenitiesOpen] = useState(false);
  const [hotelFormOpen, setHotelFormOpen] = useState(false);
  const [hotelList, setHotelList] = useState(null);
  const [hotelChoice, setHotelChoice] = useState("");
  const [mapOpen, setMapOpen] = useState(false);
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const hasGeo = !!(d.latitude && d.longitude);

  // Leaflet map — single pin at the hotel's own latitude/longitude.
  useEffect(() => {
    const L = window.L;
    if (!mapOpen || !mapRef.current || !L || !hasGeo) {
      if (mapObj.current) {
        mapObj.current.remove();
        mapObj.current = null;
      }
      return undefined;
    }
    if (mapObj.current) {
      mapObj.current.invalidateSize();
      return undefined;
    }
    const pos = [d.latitude, d.longitude];
    const map = L.map(mapRef.current, { center: pos, zoom: 15 });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);
    L.marker(pos, {
      icon: poiMarkerIcon(
        L,
        d.hotelName || activity.dashboardActivityText || "Hotel",
        d.hotelAddress,
      ),
    }).addTo(map);
    mapObj.current = map;
    const t = setTimeout(() => map.invalidateSize(), 60);
    return () => clearTimeout(t);
  }, [
    mapOpen,
    hasGeo,
    d.latitude,
    d.longitude,
    d.hotelName,
    d.hotelAddress,
    activity.dashboardActivityText,
  ]);

  const actionStyle = (enabled, primary) => ({
    minHeight: 38,
    boxSizing: "border-box",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    borderRadius: 8,
    fontFamily: "inherit",
    fontSize: 13.5,
    fontWeight: 700,
    cursor: enabled ? "pointer" : "default",
    background: !enabled ? "rgba(0,0,0,0.04)" : primary ? PURPLE_DARK : "#fff",
    color: !enabled ? "#a99483" : primary ? "#fff" : RED,
    border: `1px solid ${!enabled ? "rgba(0,0,0,0.06)" : primary ? PURPLE_DARK : RED}`,
  });

  const otpLabel =
    d.showHotelCheckinOtp === true
      ? "Check-in OTP"
      : d.showHotelCheckoutOtp === true
        ? "Check-out OTP"
        : "OTP";

  const canCancel = live && d.allowCancelHotel === true;
  const canChange = live && d.allowChangeHotel === true;

  return (
    <>
      <ImageSlider images={images} loading={imagesLoading} kind="hotel" />

      <div style={S.transporterBar}>
        <span style={S.transporterIcon}>
          <i className="fas fa-bed" style={{ fontSize: 12 }} />
        </span>
        <span
          style={{
            fontSize: 14,
            fontWeight: 800,
            color: "#fff",
            lineHeight: 1.25,
          }}
        >
          {d.hotelName || activity.dashboardActivityText}
        </span>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 8,
        }}
      >
        {d.checkinTimeLocalDisplay && (
          <InfoTile
            icon="fas fa-sign-in-alt"
            iconBg="#EDE8FD"
            iconFg={PURPLE}
            label="Check-in"
            value={d.checkinTimeLocalDisplay}
            valueStyle={{ fontSize: 14, fontWeight: 800 }}
          />
        )}
        {d.checkoutTimeLocalDisplay && (
          <InfoTile
            icon="fas fa-sign-out-alt"
            label="Check-out"
            value={d.checkoutTimeLocalDisplay}
            valueStyle={{ fontSize: 14, fontWeight: 800 }}
          />
        )}
        <InfoTile
          icon="fas fa-key"
          iconBg="#E6F4EA"
          iconFg="#1e7a45"
          label={otpLabel}
          value={
            d.displayHotelOtp && String(d.displayHotelOtp).trim()
              ? d.displayHotelOtp
              : "--"
          }
          valueStyle={{
            fontSize: 14,
            fontWeight: 800,
            color: PURPLE,
            letterSpacing: "0.1em",
          }}
        />
        <div
          onClick={() => setMapOpen(true)}
          style={{ ...S.infoCard, cursor: "pointer" }}
        >
          <span
            style={{ ...S.iconCircle, background: "#EDE8FD", color: PURPLE }}
          >
            <i className="fas fa-location-arrow" style={{ fontSize: 13 }} />
          </span>
          <div>
            <div style={S.label}>Locate</div>
            <div
              style={{
                ...S.value,
                fontSize: 14,
                fontWeight: 800,
                color: PURPLE,
              }}
            >
              View map
            </div>
          </div>
        </div>
      </div>

      {mapOpen && (
        <div
          style={{
            position: "relative",
            height: 260,
            borderRadius: 10,
            overflow: "hidden",
            background: "#E8EAE6",
            border: "1px solid #e2e0ea",
            zIndex: 0,
          }}
        >
          {hasGeo ? (
            <div ref={mapRef} style={{ position: "absolute", inset: 0 }} />
          ) : (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 7,
                color: GREY,
              }}
            >
              <i
                className="fas fa-map-marked-alt"
                style={{ fontSize: 26, color: "#b8b6c4" }}
              />
              <span style={{ fontSize: 12.5, fontWeight: 700 }}>
                Geo location not available
              </span>
            </div>
          )}
          <span
            onClick={() => setMapOpen(false)}
            title="Close map"
            style={{
              position: "absolute",
              top: 10,
              right: 10,
              zIndex: 500,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              height: 30,
              padding: "0 12px",
              borderRadius: 999,
              background: "rgba(255,255,255,0.95)",
              color: "#3c3c43",
              fontSize: 12,
              fontWeight: 800,
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(40,30,70,0.18)",
            }}
          >
            <i className="fas fa-times" style={{ fontSize: 11 }} />
            Close map
          </span>
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          alignItems: "stretch",
          gap: 8,
        }}
      >
        <button
          type="button"
          style={actionStyle(canCancel, false)}
          disabled={!canCancel}
          onClick={() =>
            canCancel &&
            handlers.onCancelHotel &&
            handlers.onCancelHotel(activity)
          }
        >
          <i className="fas fa-times" style={{ fontSize: 12 }} />
          Cancel Hotel
        </button>
        <button
          type="button"
          style={actionStyle(canChange, true)}
          disabled={!canChange}
          onClick={() => {
            if (!canChange) return;
            setHotelChoice("");
            setHotelList(null);
            setHotelFormOpen(true);
            if (handlers.onFetchAllocateHotelList) {
              Promise.resolve(handlers.onFetchAllocateHotelList(activity)).then(
                (list) => setHotelList(Array.isArray(list) ? list : []),
              );
            }
          }}
        >
          <i className="fas fa-hotel" style={{ fontSize: 12 }} />
          Change Hotel
        </button>
      </div>

      {hotelFormOpen && (
        <div style={S.formCard}>
          <div style={S.formHead}>
            <span style={S.formHeadIcon}>
              <i className="fas fa-hotel" style={{ fontSize: 11 }} />
            </span>
            <span style={S.formHeadText}>Change Hotel</span>
          </div>
          {!hotelList ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Skeleton height={9} width={90} />
              <Skeleton height={38} />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 8,
                }}
              >
                <Skeleton height={38} />
                <Skeleton height={38} />
              </div>
            </div>
          ) : (
            <>
              <label
                style={{ display: "flex", flexDirection: "column", gap: 5 }}
              >
                <span style={S.formLabel}>Select hotel</span>
                <select
                  value={hotelChoice}
                  onChange={(e) => setHotelChoice(e.target.value)}
                  style={S.select}
                >
                  <option value="">Choose a hotel</option>
                  {hotelList.map((h) => (
                    <option key={h.hotelId} value={h.hotelId}>
                      {h.hotelName}
                    </option>
                  ))}
                </select>
              </label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 8,
                }}
              >
                <button
                  type="button"
                  style={actionStyle(true, true)}
                  onClick={() => {
                    if (handlers.onChangeHotel)
                      handlers.onChangeHotel(activity, hotelChoice);
                    setHotelFormOpen(false);
                    setHotelChoice("");
                  }}
                >
                  <i className="fas fa-check" style={{ fontSize: 11 }} />
                  Submit
                </button>
                <button
                  type="button"
                  style={{
                    ...actionStyle(true, false),
                    border: "1px solid #dcd6ef",
                    color: PURPLE,
                  }}
                  onClick={() => {
                    setHotelFormOpen(false);
                    setHotelChoice("");
                  }}
                >
                  <i className="fas fa-times" style={{ fontSize: 11 }} />
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {d.hotelCancellationNote && (
        <div style={S.note}>
          <i
            className="fas fa-info-circle"
            style={{ color: PURPLE, fontSize: 13, marginTop: 2 }}
          />
          <span style={{ fontSize: 13, color: "#3c3c43", lineHeight: 1.45 }}>
            {d.hotelCancellationNote}
          </span>
        </div>
      )}
      {live && !canChange && d.hotelChangeNote && (
        <div style={S.note}>
          <i
            className="fas fa-info-circle"
            style={{ color: PURPLE, fontSize: 13, marginTop: 2 }}
          />
          <span style={{ fontSize: 13, color: "#3c3c43", lineHeight: 1.45 }}>
            {d.hotelChangeNote}
          </span>
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: 8,
        }}
      >
        {d.hotelBookingNumber && (
          <InfoTile
            icon="fas fa-hashtag"
            label="Confirmation #"
            value={d.hotelBookingNumber}
          />
        )}
        {d.address && (
          <InfoTile
            icon="fas fa-map-marker-alt"
            label="Address"
            value={d.address}
          />
        )}
        {d.hotelPhoneNumber && (
          <InfoTile
            icon="fas fa-phone-alt"
            label="Hotel phone"
            value={d.hotelPhoneNumber}
          />
        )}
        {d.hotelEmail && (
          <InfoTile
            icon="far fa-envelope"
            label="Hotel email"
            value={d.hotelEmail}
          />
        )}
        {(d.stayNight || activity.stayNight) && (
          <InfoTile
            icon="far fa-moon"
            label="Stay"
            value={d.stayNight || activity.stayNight}
          />
        )}
      </div>

      {d.transporterOption && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={S.formHeadIcon}>
              <i
                className={
                  d.transporterOption.isHotelShuttle === true
                    ? "fas fa-shuttle-van"
                    : "fas fa-car"
                }
                style={{ fontSize: 11 }}
              />
            </span>
            <span style={S.formHeadText}>Transport Option</span>
          </div>
          <div style={{ ...S.infoCard, flexWrap: "wrap", gap: "8px 18px" }}>
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 9,
                minWidth: 0,
              }}
            >
              <span style={S.iconCircle}>
                <i
                  className={
                    d.transporterOption.isHotelShuttle === true
                      ? "fas fa-shuttle-van"
                      : "fas fa-car"
                  }
                  style={{ fontSize: 13 }}
                />
              </span>
              <span
                style={{ fontSize: 13.5, fontWeight: 800, color: "#1c1c1e" }}
              >
                {d.transporterOption.displayName}
              </span>
            </span>
            {d.transporterOption.phoneNumber && (
              <a
                href={`tel:${d.transporterOption.phoneNumber}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 12.5,
                  fontWeight: 700,
                  color: ORANGE,
                }}
              >
                <i className="fas fa-phone-alt" style={{ fontSize: 10 }} />
                {d.transporterOption.phoneNumber}
              </a>
            )}
            {d.transporterOption.email && (
              <a
                href={`mailto:${d.transporterOption.email}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 12.5,
                  fontWeight: 700,
                  color: PURPLE,
                }}
              >
                <i className="far fa-envelope" style={{ fontSize: 10 }} />
                {d.transporterOption.email}
              </a>
            )}
          </div>
        </div>
      )}

      <div
        onClick={() => setCrewOpen((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          cursor: "pointer",
        }}
      >
        <span style={S.sectionTitle}>Crew Members</span>
        {!crewsLoading && crews && (
          <span style={{ fontSize: 11.5, fontWeight: 700, color: GREY }}>
            {crews.length} {crews.length === 1 ? "crew" : "crew members"}
          </span>
        )}
        <span
          style={{
            marginLeft: "auto",
            width: 24,
            height: 24,
            borderRadius: 6,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: PURPLE,
          }}
        >
          <i
            className={
              crewOpen ? "fas fa-chevron-down" : "fas fa-chevron-right"
            }
            style={{ fontSize: 11 }}
          />
        </span>
      </div>
      {!crewOpen ? null : crewsLoading || !crews ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <Skeleton height={62} style={{ flex: "1 1 260px" }} />
          <Skeleton height={62} style={{ flex: "1 1 260px" }} />
          <Skeleton height={62} style={{ flex: "1 1 260px" }} />
        </div>
      ) : crews.length === 0 ? (
        <div style={S.note}>
          <i
            className="fas fa-info-circle"
            style={{ color: PURPLE, fontSize: 13, marginTop: 2 }}
          />
          <span style={{ fontSize: 13, color: "#3c3c43", lineHeight: 1.45 }}>
            No other crew members are staying at this hotel.
          </span>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {crews.map((c) => (
            <div
              key={`${c.srNo}-${c.employeeNumber}`}
              style={{
                ...S.infoCard,
                width: "100%",
                borderLeft: `3px solid ${PURPLE}`,
                padding: "10px 14px",
                gap: 12,
              }}
            >
              <Avatar url={c.crewProfilePhotoUrl} name={c.crewName} />
              <div style={{ minWidth: 0 }}>
                <div
                  style={{ fontSize: 13.5, color: "#1c1c1e", fontWeight: 800 }}
                >
                  {c.crewName}
                </div>
                <div style={{ fontSize: 11.5, color: GREY, fontWeight: 600 }}>
                  {[c.airlineCrewType, c.employeeNumber]
                    .filter(Boolean)
                    .join(" \u00b7 ")}
                </div>
                {c.crewMobileNo && (
                  <a
                    href={`tel:${c.crewMobileNo}`}
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: ORANGE,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    <i className="fas fa-phone-alt" style={{ fontSize: 9 }} />
                    {c.crewMobileNo}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div
        onClick={() => setAmenitiesOpen((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          cursor: "pointer",
        }}
      >
        <span style={S.sectionTitle}>Hotel Amenities</span>
        {!amenitiesLoading && amenities && amenities.length > 0 && (
          <span style={{ fontSize: 11.5, fontWeight: 700, color: GREY }}>
            {amenities.length} amenities
          </span>
        )}
        <span
          style={{
            marginLeft: "auto",
            width: 24,
            height: 24,
            borderRadius: 6,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: PURPLE,
          }}
        >
          <i
            className={
              amenitiesOpen ? "fas fa-chevron-down" : "fas fa-chevron-right"
            }
            style={{ fontSize: 11 }}
          />
        </span>
      </div>
      {!amenitiesOpen ? null : amenitiesLoading || !amenities ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
            gap: 8,
          }}
        >
          <Skeleton height={48} />
          <Skeleton height={48} />
          <Skeleton height={48} />
          <Skeleton height={48} />
        </div>
      ) : d.showAmenities !== true || amenities.length === 0 ? (
        <div style={S.note}>
          <i
            className="fas fa-info-circle"
            style={{ color: PURPLE, fontSize: 13, marginTop: 2 }}
          />
          <span style={{ fontSize: 13, color: "#3c3c43", lineHeight: 1.45 }}>
            Amenities are not available for this hotel.
          </span>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
            gap: 8,
          }}
        >
          {amenities.map((am, i) => (
            <div
              key={(am.code || "am") + i}
              style={{ ...S.infoCard, padding: "9px 12px" }}
            >
              <span
                style={{
                  ...S.iconCircle,
                  width: 30,
                  height: 30,
                  background: "#EDE8FD",
                  color: PURPLE,
                }}
              >
                <i
                  className={AMENITY_ICON[am.code] || AMENITY_ICON.other}
                  style={{ fontSize: 12 }}
                />
              </span>
              <span
                style={{ fontSize: 12.5, fontWeight: 700, color: "#1c1c1e" }}
              >
                {am.name}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const HomeStayDetail = ({ activity, handlers = {} }) => {
  const d = camelKeys(activity.homeDetails || {});
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const [addressFormOpen, setAddressFormOpen] = useState(false);
  const [addressList, setAddressList] = useState(null);
  const [addressChoice, setAddressChoice] = useState("");

  // Leaflet map of the residence (homeDetails.latitude / longitude)
  useEffect(() => {
    const L = window.L;
    if (!L || !mapRef.current || !hasGeo(d.latitude, d.longitude))
      return undefined;
    if (mapObj.current) {
      mapObj.current.invalidateSize();
      return undefined;
    }
    const map = L.map(mapRef.current, {
      center: [d.latitude, d.longitude],
      zoom: 15,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);
    L.marker([d.latitude, d.longitude], {
      icon: poiMarkerIcon(L, d.headerTitle || "Residence", d.address),
    }).addTo(map);
    mapObj.current = map;
    const t = setTimeout(() => map.invalidateSize(), 60);
    return () => {
      clearTimeout(t);
      map.remove();
      mapObj.current = null;
    };
  }, [d.latitude, d.longitude, d.headerTitle, d.address]);

  const actionStyle = (primary) => ({
    minHeight: 38,
    boxSizing: "border-box",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    borderRadius: 8,
    fontFamily: "inherit",
    fontSize: 13.5,
    fontWeight: 700,
    cursor: "pointer",
    background: primary ? PURPLE_DARK : "#fff",
    color: primary ? "#fff" : PURPLE,
    border: `1px solid ${primary ? PURPLE_DARK : "#dcd6ef"}`,
  });

  const tiles = [
    {
      icon: "fas fa-sign-in-alt",
      iconBg: "#EDE8FD",
      iconFg: PURPLE,
      label: "Reached residence",
      value: d.checkinTimeLocalDisplay,
    },
    {
      icon: "fas fa-sign-out-alt",
      iconBg: "#FDEBDF",
      iconFg: ORANGE,
      label: "Pick-up from residence",
      value: d.checkoutTimeLocalDisplay,
    },
    {
      icon: "far fa-moon",
      iconBg: "#E6F4EA",
      iconFg: "#1e7a45",
      label: "Stay",
      value: activity.stayNight,
    },
  ].filter((t) => t.value);

  const allowChange = d.allowChangeAddress === true;
  const canChange = allowChange && !SEGMENT_ACTION_NON_CLICKABLE;

  return (
    <>
      {hasGeo(d.latitude, d.longitude) ? (
        <div
          style={{
            position: "relative",
            height: 240,
            borderRadius: 10,
            overflow: "hidden",
            background: "#E8EAE6",
            border: "1px solid #e2e0ea",
            zIndex: 0,
          }}
        >
          <div ref={mapRef} style={{ position: "absolute", inset: 0 }} />
        </div>
      ) : (
        <NoGeo height={240} />
      )}

      <div style={S.transporterBar}>
        <span style={S.transporterIcon}>
          <i className="fas fa-home" style={{ fontSize: 12 }} />
        </span>
        <span
          style={{
            fontSize: 14,
            fontWeight: 800,
            color: "#fff",
            lineHeight: 1.25,
          }}
        >
          {d.headerTitle || "Residence"}
        </span>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 8,
        }}
      >
        {tiles.map((t) => (
          <InfoTile
            key={t.label}
            icon={t.icon}
            iconBg={t.iconBg}
            iconFg={t.iconFg}
            label={t.label}
            value={t.value}
            valueStyle={{ fontSize: 14, fontWeight: 800 }}
          />
        ))}
      </div>

      {d.address && (
        <div style={{ ...S.infoCard, alignItems: "center" }}>
          <span style={S.iconCircle}>
            <i className="fas fa-map-marker-alt" style={{ fontSize: 13 }} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={S.label}>Address</div>
            <div style={S.value}>{d.address}</div>
            {!canChange && d.changeAddressNote && (
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  marginTop: 5,
                  alignItems: "flex-start",
                }}
              >
                <i
                  className="fas fa-info-circle"
                  style={{ color: PURPLE, fontSize: 11, marginTop: 2 }}
                />
                <span
                  style={{ fontSize: 11.5, color: "#6a6a75", lineHeight: 1.4 }}
                >
                  {d.changeAddressNote}
                </span>
              </div>
            )}
          </div>
          {allowChange && (
            <button
              type="button"
              disabled={!canChange}
              onClick={() => {
                if (!canChange) return;
                setAddressChoice("");
                setAddressList(null);
                setAddressFormOpen(true);
                if (handlers.onFetchStationAddress) {
                  Promise.resolve(
                    handlers.onFetchStationAddress(activity, "home"),
                  ).then((list) =>
                    setAddressList(Array.isArray(list) ? list : []),
                  );
                }
              }}
              style={{
                marginLeft: "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                height: 30,
                padding: "0 11px",
                borderRadius: 8,
                fontFamily: "inherit",
                fontSize: 12,
                fontWeight: 800,
                whiteSpace: "nowrap",
                cursor: canChange ? "pointer" : "not-allowed",
                border: `1px solid ${canChange ? PURPLE : "#c9c9d1"}`,
                background: canChange ? "#fff" : "#f2f2f5",
                color: canChange ? PURPLE : "#9a9aa5",
                opacity: canChange ? 1 : 0.7,
              }}
            >
              <i className="fas fa-pen" style={{ fontSize: 10 }} />
              Change
            </button>
          )}
        </div>
      )}

      {addressFormOpen && (
        <div style={S.formCard}>
          <div style={S.formHead}>
            <span style={S.formHeadIcon}>
              <i className="fas fa-map-marker-alt" style={{ fontSize: 11 }} />
            </span>
            <span style={S.formHeadText}>Change Residence Address</span>
          </div>
          {!addressList ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Skeleton height={9} width={100} />
              <Skeleton height={38} />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 8,
                }}
              >
                <Skeleton height={38} />
                <Skeleton height={38} />
              </div>
            </div>
          ) : (
            <>
              <label
                style={{ display: "flex", flexDirection: "column", gap: 5 }}
              >
                <span style={S.formLabel}>Select address</span>
                <select
                  value={addressChoice}
                  onChange={(e) => setAddressChoice(e.target.value)}
                  style={S.select}
                >
                  <option value="">Choose an address</option>
                  {addressList.map((s) => (
                    <option key={s.id} value={s.id}>
                      {[s.name, s.address].filter(Boolean).join(" \u2014 ")}
                    </option>
                  ))}
                </select>
              </label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 8,
                }}
              >
                <button
                  type="button"
                  style={actionStyle(true)}
                  onClick={() => {
                    if (handlers.onChangeHomeAddress)
                      handlers.onChangeHomeAddress(activity, addressChoice);
                    setAddressFormOpen(false);
                    setAddressChoice("");
                  }}
                >
                  <i className="fas fa-check" style={{ fontSize: 11 }} />
                  Submit
                </button>
                <button
                  type="button"
                  style={actionStyle(false)}
                  onClick={() => {
                    setAddressFormOpen(false);
                    setAddressChoice("");
                  }}
                >
                  <i className="fas fa-times" style={{ fontSize: 11 }} />
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

const FlightDetail = ({ activity, crews, crewsLoading }) => {
  const d = camelKeys(activity.flightDetails || {});
  const [crewOpen, setCrewOpen] = useState(false);
  const dash = (v) => (v && String(v).trim() ? v : "--");

  const statusBg =
    d.flightStatus === "Landed"
      ? "#1FA463"
      : d.flightStatus === "Cancelled"
        ? "#E01B24"
        : "#FFB300";
  const statusFg =
    d.flightStatus === "Scheduled" || !d.flightStatus ? "#2A1A6B" : "#fff";
  const statusIcon =
    d.flightStatus === "Landed"
      ? "fas fa-check-circle"
      : d.flightStatus === "Cancelled"
        ? "fas fa-times-circle"
        : "far fa-clock";

  const weather = [d.departureWeather, d.arrivalWeather].filter(Boolean);

  const TimeRow = ({ icon, label, value, color }) => (
    <span>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12.5,
          fontWeight: 600,
          color: GREY,
        }}
      >
        <i className={icon} style={{ fontSize: 11 }} />
        {label}
      </span>
      <span
        style={{
          display: "block",
          fontSize: 13,
          fontWeight: 800,
          color: color || "#1c1c1e",
        }}
      >
        {value}
      </span>
    </span>
  );

  return (
    <>
      <div
        style={{
          background: PURPLE,
          borderRadius: 12,
          padding: "14px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.22)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <i className="fas fa-plane" style={{ fontSize: 14 }} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 15,
                fontWeight: 800,
                color: "#fff",
                lineHeight: 1.2,
              }}
            >
              {d.airlineName}
            </div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "rgba(255,255,255,0.82)",
              }}
            >
              {d.flightNumber}
            </div>
          </div>
          {d.flightStatus && (
            <span
              style={{
                marginLeft: "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                height: 28,
                padding: "0 12px",
                borderRadius: 999,
                fontSize: 12.5,
                fontWeight: 800,
                background: statusBg,
                color: statusFg,
              }}
            >
              <i className={statusIcon} style={{ fontSize: 11 }} />
              {d.flightStatus}
            </span>
          )}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 30,
                fontWeight: 800,
                color: "#fff",
                lineHeight: 1,
              }}
            >
              {d.departureAirportCode}
            </div>
            <div
              style={{
                fontSize: 12.5,
                fontWeight: 700,
                color: "rgba(255,255,255,0.85)",
                lineHeight: 1.35,
                marginTop: 5,
                textWrap: "pretty",
              }}
            >
              {d.departureAirportName}
            </div>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexShrink: 0,
            }}
          >
            <span
              style={{
                width: 34,
                borderTop: "1px dashed rgba(255,255,255,0.6)",
              }}
            />
            <i
              className="fas fa-plane"
              style={{ fontSize: 15, color: "#fff" }}
            />
            <span
              style={{
                width: 34,
                borderTop: "1px dashed rgba(255,255,255,0.6)",
              }}
            />
          </div>
          <div style={{ minWidth: 0, textAlign: "right" }}>
            <div
              style={{
                fontSize: 30,
                fontWeight: 800,
                color: "#fff",
                lineHeight: 1,
              }}
            >
              {d.arrivalAirportCode}
            </div>
            <div
              style={{
                fontSize: 12.5,
                fontWeight: 700,
                color: "rgba(255,255,255,0.85)",
                lineHeight: 1.35,
                marginTop: 5,
                textWrap: "pretty",
              }}
            >
              {d.arrivalAirportName}
            </div>
          </div>
        </div>

        <div
          style={{
            borderTop: "1px solid rgba(255,255,255,0.25)",
            paddingTop: 10,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "8px 16px",
            fontSize: 12.5,
            fontWeight: 700,
            color: "#fff",
          }}
        >
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: 7 }}
          >
            <i className="far fa-calendar-alt" style={{ fontSize: 11 }} />
            {d.flightDateDisplay}
          </span>
          <span
            style={{
              width: 1,
              height: 14,
              background: "rgba(255,255,255,0.35)",
            }}
          />
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: 7 }}
          >
            <i className="far fa-clock" style={{ fontSize: 11 }} />
            Duration: {dash(d.durationText)}
          </span>
        </div>
      </div>

      <div
        style={{
          background: "#fff",
          border: "1px solid #f0eff5",
          borderRadius: 12,
          padding: "12px 14px",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: PURPLE,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <i className="fas fa-plane" style={{ fontSize: 12 }} />
          </span>
          <span style={{ fontSize: 14, fontWeight: 800, color: PURPLE }}>
            Flight Timeline
          </span>
        </div>

        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontSize: 12.5,
              fontWeight: 600,
              color: GREY,
            }}
          >
            <span
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <i className="fas fa-plane-departure" style={{ fontSize: 11 }} />
              Departure
            </span>
            <span
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              Arrival
              <i className="fas fa-plane-arrival" style={{ fontSize: 11 }} />
            </span>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "auto 1fr auto",
              alignItems: "center",
              gap: 10,
              marginTop: 4,
            }}
          >
            <span
              style={{
                fontSize: 26,
                fontWeight: 800,
                color: PURPLE,
                lineHeight: 1.1,
              }}
            >
              {dash(d.scheduleDepartureDateTime ?? d.departureTimeLocalDisplay)}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: PURPLE,
                  flexShrink: 0,
                }}
              />
              <span
                style={{ flex: "1 1 0", borderTop: "1px dashed #cfc7e6" }}
              />
              <i
                className="fas fa-plane"
                style={{ color: PURPLE, fontSize: 14 }}
              />
              <span
                style={{ flex: "1 1 0", borderTop: "1px dashed #cfc7e6" }}
              />
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: PURPLE,
                  flexShrink: 0,
                }}
              />
            </span>
            <span
              style={{
                fontSize: 26,
                fontWeight: 800,
                color: PURPLE,
                lineHeight: 1.1,
              }}
            >
              {dash(d.scheduleArrivalDateTime ?? d.arrivalTimeLocalDisplay)}
            </span>
          </div>
          {(d.scheduleDepartureDateDisplay || d.scheduleArrivalDateDisplay) && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontSize: 12.5,
                fontWeight: 600,
                color: GREY,
              }}
            >
              <span>{dash(d.scheduleDepartureDateDisplay)}</span>
              <span>{dash(d.scheduleArrivalDateDisplay)}</span>
            </div>
          )}
        </div>

        <div
          style={{
            borderTop: "1px solid #f0eff5",
            paddingTop: 11,
            display: "grid",
            gridTemplateColumns: "1fr 1px 1fr",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            <TimeRow
              icon="far fa-clock"
              label="Estimated departure"
              value={dash(d.estDepartureDateTime)}
            />
            <TimeRow
              icon="fas fa-plane-departure"
              label="Actual departure"
              value={dash(d.actualDepartureDateTime)}
              color={d.actualDepartureDateTime ? "#1e7a45" : GREY}
            />
            <TimeRow
              icon="fas fa-map-marker-alt"
              label="Terminal"
              value={dash(d.departureTerminal)}
            />
            <TimeRow
              icon="fas fa-door-open"
              label="Gate"
              value={dash(d.departureGate)}
            />
          </div>
          <span style={{ background: "#f0eff5" }} />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 7,
              textAlign: "right",
            }}
          >
            <TimeRow
              icon="far fa-clock"
              label="Estimated arrival"
              value={dash(d.estArrivalDateTime)}
            />
            <TimeRow
              icon="fas fa-plane-arrival"
              label="Actual arrival"
              value={dash(d.actualArrivalDateTime)}
              color={d.actualArrivalDateTime ? "#1e7a45" : GREY}
            />
            <TimeRow
              icon="fas fa-map-marker-alt"
              label="Terminal"
              value={dash(d.arrivalTerminal)}
            />
            <TimeRow
              icon="fas fa-door-open"
              label="Gate"
              value={dash(d.arrivalGate)}
            />
          </div>
        </div>
      </div>

      {weather.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: 10,
          }}
        >
          {weather.map((w) => (
            <div
              key={w.airportCode}
              style={{
                background: "#fff",
                border: "1px solid #f0eff5",
                borderRadius: 12,
                padding: "12px 14px",
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <i
                    className="fas fa-map-marker-alt"
                    style={{ color: PURPLE, fontSize: 12 }}
                  />
                  <span
                    style={{ fontSize: 14, fontWeight: 800, color: PURPLE }}
                  >
                    {w.airportCode}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: GREY,
                    lineHeight: 1.35,
                    marginTop: 3,
                    textWrap: "pretty",
                  }}
                >
                  {w.airportName}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <i
                  className={
                    WEATHER_ICON[w.conditionCode] || WEATHER_ICON.other
                  }
                  style={{
                    fontSize: 30,
                    color:
                      WEATHER_COLOR[w.conditionCode] || WEATHER_COLOR.other,
                  }}
                />
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 24,
                      fontWeight: 800,
                      color: "#1c1c1e",
                      lineHeight: 1,
                    }}
                  >
                    {w.temperature}
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: GREY,
                      marginTop: 3,
                    }}
                  >
                    {w.condition}
                  </div>
                </div>
              </div>
              <div
                style={{
                  borderTop: "1px solid #f0eff5",
                  paddingTop: 9,
                  display: "flex",
                  gap: 14,
                }}
              >
                {[
                  ["fas fa-wind", "Wind", w.windSpeed],
                  ["fas fa-cloud", "Clouds", w.clouds],
                ].map(([icon, label, value]) => (
                  <span
                    key={label}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      flex: "1 1 0",
                      minWidth: 0,
                    }}
                  >
                    <i className={icon} style={{ color: GREY, fontSize: 12 }} />
                    <span style={{ minWidth: 0 }}>
                      <span
                        style={{
                          display: "block",
                          fontSize: 11.5,
                          fontWeight: 600,
                          color: GREY,
                        }}
                      >
                        {label}
                      </span>
                      <span
                        style={{
                          display: "block",
                          fontSize: 13,
                          fontWeight: 800,
                          color: "#1c1c1e",
                        }}
                      >
                        {value}
                      </span>
                    </span>
                  </span>
                ))}
              </div>
              {w.dayLabel && (
                <div
                  style={{
                    borderTop: "1px solid #f0eff5",
                    paddingTop: 9,
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: GREY,
                  }}
                >
                  <i className="far fa-calendar" style={{ fontSize: 11 }} />
                  {w.dayLabel}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div
        onClick={() => setCrewOpen((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          cursor: "pointer",
        }}
      >
        <span style={S.sectionTitle}>Crew Members</span>
        {!crewsLoading && crews && (
          <span style={{ fontSize: 11.5, fontWeight: 700, color: GREY }}>
            {crews.length} {crews.length === 1 ? "crew" : "crew members"}
          </span>
        )}
        <span
          style={{
            marginLeft: "auto",
            width: 24,
            height: 24,
            borderRadius: 6,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: PURPLE,
          }}
        >
          <i
            className={
              crewOpen ? "fas fa-chevron-down" : "fas fa-chevron-right"
            }
            style={{ fontSize: 11 }}
          />
        </span>
      </div>
      {!crewOpen ? null : crewsLoading || !crews ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <Skeleton height={62} style={{ flex: "1 1 260px" }} />
          <Skeleton height={62} style={{ flex: "1 1 260px" }} />
          <Skeleton height={62} style={{ flex: "1 1 260px" }} />
        </div>
      ) : crews.length === 0 ? (
        <div style={S.note}>
          <i
            className="fas fa-info-circle"
            style={{ color: PURPLE, fontSize: 13, marginTop: 2 }}
          />
          <span style={{ fontSize: 13, color: "#3c3c43", lineHeight: 1.45 }}>
            No crew members are assigned to this leg.
          </span>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {crews.map((c) => (
            <div
              key={`${c.srNo}-${c.employeeNumber}`}
              style={{
                ...S.infoCard,
                width: "100%",
                borderLeft: `3px solid ${PURPLE}`,
                padding: "10px 14px",
                gap: 12,
              }}
            >
              <Avatar url={c.crewProfilePhotoUrl} name={c.crewName} />
              <div style={{ minWidth: 0 }}>
                <div
                  style={{ fontSize: 13.5, color: "#1c1c1e", fontWeight: 800 }}
                >
                  {c.crewName}
                </div>
                <div style={{ fontSize: 11.5, color: GREY, fontWeight: 600 }}>
                  {[c.airlineCrewType, c.employeeNumber]
                    .filter(Boolean)
                    .join(" \u00b7 ")}
                </div>
                {c.crewMobileNo && (
                  <a
                    href={`tel:${c.crewMobileNo}`}
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: ORANGE,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    <i className="fas fa-phone-alt" style={{ fontSize: 9 }} />
                    {c.crewMobileNo}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const OtherSegmentDetail = ({ activity }) => {
  const kind = kindOf(activity);
  const isDutyKind = kind === "groundDuty" || kind === "airportDuty";
  const g = isDutyKind
    ? camelKeys(activity.groundDutyDetails || activity.airportDutyDetails || {})
    : {};
  const [mapOpen, setMapOpen] = useState(false);
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const hasCoords = hasGeo(g.latitude, g.longitude);

  // Leaflet map for the duty location — Ground Duty / Airport Duty only.
  useEffect(() => {
    const L = window.L;
    if (!mapOpen || !mapRef.current || !L || !hasCoords) {
      if (mapObj.current) {
        mapObj.current.remove();
        mapObj.current = null;
      }
      return undefined;
    }
    if (mapObj.current) {
      mapObj.current.invalidateSize();
      return undefined;
    }
    const pos = [g.latitude, g.longitude];
    const map = L.map(mapRef.current, { center: pos, zoom: 15 });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);
    L.marker(pos, { icon: poiMarkerIcon(L, g.headerTitle, g.address) }).addTo(
      map,
    );
    mapObj.current = map;
    const t = setTimeout(() => map.invalidateSize(), 60);
    return () => clearTimeout(t);
  }, [mapOpen, hasCoords, g.latitude, g.longitude, g.headerTitle, g.address]);

  const rows = [];
  if (isDutyKind) {
    rows.push({
      icon: "fas fa-code",
      label: "Activity location code",
      value: g.activityLocationCode,
    });
    rows.push({
      icon: "fas fa-building",
      label: "Activity location",
      value: g.activityLocation,
    });
    rows.push({
      icon: "fas fa-map-marker-alt",
      label: "Address",
      value: g.address,
    });
    rows.push({
      icon: "fas fa-play",
      label: "Start (local)",
      value: g.activityStartTimeLocalDisplay,
    });
    rows.push({
      icon: "fas fa-stop",
      label: "End (local)",
      value: g.activityEndTimeLocalDisplay,
    });
    rows.push({
      icon: "fas fa-globe",
      label: "Start / End (GMT)",
      value:
        g.activityStartTimeGmtDisplay && g.activityEndTimeGmtDisplay
          ? `${g.activityStartTimeGmtDisplay} \u2013 ${g.activityEndTimeGmtDisplay}`
          : "",
    });
  } else if (kind === "home") {
    const h = activity.homeDetails || {};
    rows.push({
      icon: "fas fa-map-marker-alt",
      label: "Address",
      value: h.address,
    });
    rows.push({
      icon: "fas fa-sign-in-alt",
      label: "Check-in (local)",
      value: h.checkinTimeLocalDisplay,
    });
    rows.push({
      icon: "fas fa-sign-out-alt",
      label: "Check-out (local)",
      value: h.checkoutTimeLocalDisplay,
    });
    rows.push({
      icon: "far fa-moon",
      label: "Stay",
      value: activity.stayNight,
    });
  } else if (kind === "hotel") {
    rows.push({
      icon: "fas fa-bed",
      label: "Hotel",
      value: activity.dashboardActivityText,
    });
    rows.push({
      icon: "fas fa-hashtag",
      label: "Confirmation #",
      value: activity.hotelBookingNumber,
    });
    rows.push({
      icon: "far fa-moon",
      label: "Stay",
      value: activity.stayNight,
    });
  } else if (kind === "flight") {
    rows.push({
      icon: "fas fa-plane",
      label: "Sector",
      value: activity.dashboardActivityText,
    });
    rows.push({
      icon: "fas fa-clock",
      label: "Timing",
      value: activity.dashboardActivityLocalTimeValue,
    });
    rows.push({
      icon: "fas fa-door-open",
      label: "Flight info",
      value: activity.activityInfo,
    });
  }

  return (
    <>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: 8,
        }}
      >
        {rows
          .filter((r) => r.value)
          .map((r) => (
            <InfoTile
              key={r.label}
              icon={r.icon}
              label={r.label}
              value={r.value}
            />
          ))}
        {isDutyKind && (
          <div
            onClick={() => setMapOpen(true)}
            style={{ ...S.infoCard, cursor: "pointer" }}
          >
            <span
              style={{ ...S.iconCircle, background: "#EDE8FD", color: PURPLE }}
            >
              <i className="fas fa-location-arrow" style={{ fontSize: 13 }} />
            </span>
            <div>
              <div style={S.label}>Locate</div>
              <div
                style={{
                  ...S.value,
                  fontSize: 14,
                  fontWeight: 800,
                  color: PURPLE,
                }}
              >
                View map
              </div>
            </div>
          </div>
        )}
      </div>

      {isDutyKind && mapOpen && (
        <div
          style={{
            position: "relative",
            height: 260,
            borderRadius: 10,
            overflow: "hidden",
            background: "#E8EAE6",
            border: "1px solid #e2e0ea",
            zIndex: 0,
          }}
        >
          {hasCoords ? (
            <div ref={mapRef} style={{ position: "absolute", inset: 0 }} />
          ) : (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 7,
                color: GREY,
              }}
            >
              <i
                className="fas fa-map-marked-alt"
                style={{ fontSize: 26, color: "#b8b6c4" }}
              />
              <span style={{ fontSize: 12.5, fontWeight: 700 }}>
                Geo location not available
              </span>
            </div>
          )}
          <span
            onClick={() => setMapOpen(false)}
            title="Close map"
            style={{
              position: "absolute",
              top: 10,
              right: 10,
              zIndex: 500,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              height: 30,
              padding: "0 12px",
              borderRadius: 999,
              background: "rgba(255,255,255,0.95)",
              color: "#3c3c43",
              fontSize: 12,
              fontWeight: 800,
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(40,30,70,0.18)",
            }}
          >
            <i className="fas fa-times" style={{ fontSize: 11 }} />
            Close map
          </span>
        </div>
      )}
    </>
  );
};

const SegmentCard = ({
  activity,
  expanded,
  onToggle,
  detailState,
  handlers,
  employeeNumber,
}) => {
  const kind = kindOf(activity);
  const dash = dashColor(activity);
  const cancelled = segmentCancelled(activity);
  const isLiveTransport =
    kind === "transport" && activity.isLiveTracking === true;
  const bareHeader =
    activity.isNonFlyActivity &&
    (kind === "groundDuty" || kind === "airportDuty");
  const timeValue = activity.showGMTDateTime
    ? activity.dashboardActivityGmtTimeValue ||
      activity.dashboardActivityLocalTimeValue
    : activity.dashboardActivityLocalTimeValue;

  const meta = [];
  if (activity.airportCode)
    meta.push(
      <MetaPair key="apt" iconClass="fas fa-location-arrow" iconColor={PURPLE}>
        {activity.airportCode}
      </MetaPair>,
    );
  if (activity.showActivityInfo && activity.activityInfo) {
    if (meta.length) meta.push(<Dot key="d1" />);
    meta.push(
      <MetaPair
        key="info"
        iconClass={
          activity.isDepartureFlightInfo
            ? "fas fa-plane-departure"
            : activity.isArrivalFlightInfo
              ? "fas fa-plane-arrival"
              : "fas fa-route"
        }
        iconColor={GREY}
      >
        {activity.activityInfo}
      </MetaPair>,
    );
  }
  if (
    isTransportKind(kind) &&
    activity.showDutyReportingTime &&
    activity.dutyReportingTimeDisplay
  ) {
    if (meta.length) meta.push(<Dot key="d2" />);
    meta.push(
      <MetaPair key="rep" iconClass="fas fa-clock" iconColor={PURPLE}>
        {activity.dutyReportingTimeDisplay}
      </MetaPair>,
    );
  }
  if (
    isTransportKind(kind) &&
    activity.showDeBriefingTime &&
    activity.deBriefingTimeDisplay
  ) {
    if (meta.length) meta.push(<Dot key="d3" />);
    meta.push(
      <MetaPair key="deb" iconClass="fas fa-sign-out-alt" iconColor={PURPLE}>
        {activity.deBriefingTimeDisplay}
      </MetaPair>,
    );
  }
  if (activity.stayNight) {
    if (meta.length) meta.push(<Dot key="d4" />);
    meta.push(
      <MetaPair key="stay" iconClass="far fa-moon" iconColor={PURPLE}>
        {activity.stayNight}
      </MetaPair>,
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div
        onClick={onToggle}
        className={`ca-segment-card${isLiveTransport ? " is-live-tracking" : ""}`}
        style={{
          borderRadius: 12,
          padding: "11px 26px 11px 14px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
          position: "relative",
          overflow: "hidden",
          cursor: "pointer",
          background: isLiveTransport
            ? CARD_LIVE_TRACKING
            : cardBackground(activity),
          boxShadow:
            activity.isNonFlyActivity || cancelled
              ? "none"
              : "0 1px 4px rgba(0,0,0,0.10)",
        }}
      >
        <span title={expanded ? "Collapse" : "Expand"} style={S.chevron}>
          <i
            className={
              expanded ? "fas fa-chevron-down" : "fas fa-chevron-right"
            }
            style={{ fontSize: 12 }}
          />
        </span>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {!bareHeader && (
            <i
              className={KIND_ICON[kind]}
              style={{ color: "#F0A070", fontSize: 14 }}
            />
          )}
          <span
            style={{
              fontSize: 13,
              color: GREY,
              fontWeight: 600,
              whiteSpace: "nowrap",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {activity.dashboardActivityLabel}
          </span>
          {timeValue && (
            <span
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                gap: 5,
                fontSize: 12,
                fontWeight: 700,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              <i
                className="fas fa-clock"
                style={{ color: PURPLE, fontSize: 11 }}
              />
              <span style={{ color: "#3c3c43" }}>{timeValue}</span>
              <Dot />
              <i
                className="fas fa-exchange-alt"
                style={{ color: PURPLE, fontSize: 10 }}
              />
              <span style={{ color: PURPLE, fontWeight: 800 }}>
                {activity.dashboardActivityTimeLabel}
              </span>
            </span>
          )}
        </div>

        <div style={{ borderTop: `1px dashed ${dash}` }} />
        <div
          style={{
            fontSize: 13.5,
            fontWeight: 800,
            color: "#1c1c1e",
            lineHeight: 1.35,
          }}
        >
          {activity.dashboardActivityText}
        </div>
        <div style={{ borderTop: `1px dashed ${dash}` }} />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexWrap: "wrap",
            fontSize: 12,
            color: GREY,
            fontWeight: 600,
          }}
        >
          {meta}
          {isLiveTransport && activity.trackingStatus && (
            <span
              style={{
                marginLeft: "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                fontSize: 11.5,
                fontWeight: 800,
                color: GREEN_DARK,
                whiteSpace: "nowrap",
              }}
            >
              <i className="fas fa-car-side" style={{ fontSize: 10 }} />
              {activity.trackingStatus}
            </span>
          )}
        </div>

        {cancelled && (
          <div style={S.stamp}>
            <span style={S.stampText}>CANCELLED</span>
          </div>
        )}
      </div>

      {expanded && (
        <div style={S.panel}>
          {isTransportKind(kind) ? (
            <TransportDetail
              activity={activity}
              handlers={handlers}
              employeeNumber={employeeNumber}
              {...detailState}
            />
          ) : kind === "hotel" ? (
            <HotelStayDetail
              activity={activity}
              handlers={handlers}
              images={detailState.images}
              imagesLoading={detailState.imagesLoading}
              crews={detailState.crews}
              crewsLoading={detailState.tripLoading}
              amenities={detailState.amenities}
              amenitiesLoading={detailState.amenitiesLoading}
            />
          ) : kind === "flight" ? (
            <FlightDetail
              activity={activity}
              crews={detailState.crews}
              crewsLoading={detailState.tripLoading}
            />
          ) : kind === "home" ? (
            <HomeStayDetail activity={activity} handlers={handlers} />
          ) : (
            <OtherSegmentDetail activity={activity} />
          )}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------------- */
/* Default API wiring — CreweasyMobile controller                            */
/* ------------------------------------------------------------------------- */

const paramTimeOf = (activity) => {
  const kind = kindOf(activity);
  if (kind === "flight") return activity.flightDetails?.crewsParamTime;
  if (kind === "hotel") return activity.hotelDetails?.crewsParamTime;
  if (kind === "hotelTransport")
    return activity.hotelTransportDetails?.crewsParamTime;
  return activity.localTransportDetails?.crewsParamTime;
};

const txnOf = (activity) => {
  const kind = kindOf(activity);
  const d =
    kind === "hotel"
      ? activity.hotelDetails
      : kind === "hotelTransport"
        ? activity.hotelTransportDetails
        : activity.localTransportDetails;
  return d?.transactionId ?? activity.transactionId;
};

/** GET leg-crews — flightScheduleTime = flightDetails.crewsParamTime */
const defaultFetchLegCrews = (activity) =>
  getLegCrews({ flightScheduleTime: paramTimeOf(activity) });

/** GET hotel-crews — checkinTime = hotelDetails.crewsParamTime */
const defaultFetchHotelCrews = (activity) =>
  getHotelCrews({ checkinTime: paramTimeOf(activity) });

/** GET hotel-transport-crews — pickupTime = hotelTransportDetails.crewsParamTime */
const defaultFetchHotelTransportCrews = (activity) =>
  getHotelTransportCrews({ pickupTime: paramTimeOf(activity) });

/** GET get-trip-driver (Transport detail view) — response's `eta` object already
 * carries isBoarded / trackingMessage / location, so no separate eta call is needed. */
const defaultFetchTripDriver = async (activity, employeeNumber) => {
  const transactionId = txnOf(activity);
  const trip = await getTripDriver({ transactionId, employeeNumber });
  return {
    driverInfo: trip?.driverInfo ?? trip,
    crews: trip?.crews ?? [],
    eta: trip?.eta ?? null,
  };
};

/**
 * GET get-trip-tracking (Track button map) — returns { driverInfo, passengers, destination,
 * route, eta }. `route` (mapJson/wayPoints) draws the real road route via tripRoute.js, and `eta`
 * seeds the single current-leg ETA/ETD message + progress dial — refreshed only when this
 * endpoint itself re-fires (isReloadMap or client-detected off-route replot), never via a
 * separate ETA-only call. Same cost-safe pattern as LiveTripTracking.jsx.
 */
const defaultFetchTripTracking = (activity, employeeNumber) =>
  getTripTracking({ transactionId: txnOf(activity), employeeNumber });

/** GET get-vehicle-gps-location — { latitude, longitude, speed, bearing, altitude, gpsTime,
 * isReloadMap, isGetEta }. Polled every 10s while the Track map is open to move/rotate the live
 * vehicle marker, and (via isReloadMap) to learn whether the vehicle has moved far enough
 * (>= 50m, computed server-side) to be worth reloading the route. isGetEta is intentionally
 * unread — there is no separate ETA call to gate. */
const defaultFetchVehicleGpsLocation = (activity, employeeNumber) =>
  getVehicleGpsLocation({ transactionId: txnOf(activity), employeeNumber });

/** GET get-trip-security-guard */
const defaultFetchSecurityGuard = (activity, employeeNumber) =>
  getTripSecurityGuard({ transactionId: txnOf(activity), employeeNumber });

/** POST get-allocate-hotel-list */
const defaultFetchAllocateHotelList = (activity) => {
  const kind = kindOf(activity);
  const d =
    kind === "hotel" ? activity.hotelDetails : activity.hotelTransportDetails;
  return getAllocateHotelList({
    transactionId: txnOf(activity),
    locationCode:
      d?.activityLocationCode ?? activity.activityLocationCode ?? "",
  });
};

/** GET station-address */
const defaultFetchStationAddress = (activity) =>
  getStationAddress({ airportCode: activity.airportCode });

export const CrewActivityMobileViewComponent = ({
  dashboardData = [],
  employeeNumber = "",
  airlineCode = "",
  recordCount,
  isLoading = false,
  onRefresh,
  onBack,
  // each fetcher defaults to its CreweasyMobile endpoint; pass a prop to override
  fetchSegmentImages = getSegmentImages,
  fetchTripDriver = defaultFetchTripDriver,
  fetchTripTracking = defaultFetchTripTracking,
  fetchVehicleGpsLocation = defaultFetchVehicleGpsLocation,
  fetchTripSecurityGuard = defaultFetchSecurityGuard,
  fetchHotelTransportCrews = defaultFetchHotelTransportCrews,
  fetchHotelCrews = defaultFetchHotelCrews,
  fetchHotelAmenities,
  fetchLegCrews = defaultFetchLegCrews,
  fetchCrewDropdown,
  onSelectStaff,
  onCancelRide,
  onReadyForPickup,
  onEarlyPickup,
  onChangePickupAddress,
  onChangeDropoffAddress,
  onTrackVehicle,
  onCancelHotel,
  onChangeHotel,
  onChangeHomeAddress,
  onFetchAllocateHotelList = defaultFetchAllocateHotelList,
  onFetchEarlyPickupTimes,
  onFetchStationAddress = defaultFetchStationAddress,
}) => {
  const [openKey, setOpenKey] = useState(null);
  const [detail, setDetail] = useState({
    images: null,
    imagesLoading: false,
    amenities: null,
    amenitiesLoading: false,
    driver: null,
    crews: null,
    eta: null,
    tripLoading: false,
    driverRefreshing: false,
    guard: null,
    guardLoading: false,
    tracking: null,
    trackingLoading: false,
  });

  // camelKeys() so a PascalCase API response renders identically
  const days = useMemo(
    () =>
      camelKeys([...(dashboardData ?? [])]).sort(
        (a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0),
      ),
    [dashboardData],
  );

  const total =
    recordCount ??
    days.reduce((sum, d) => sum + ((d.activities ?? []).length || 0), 0);

  const loadDetail = useCallback(
    async (activity) => {
      const kind = kindOf(activity);
      if (kind === "other") return;

      setDetail({
        images: null,
        imagesLoading: true,
        amenities: null,
        amenitiesLoading: kind === "hotel",
        driver: null,
        crews: null,
        eta: null,
        tripLoading: true,
        driverRefreshing: false,
        guard: null,
        guardLoading: kind === "transport",
        tracking: null,
        trackingLoading: false,
      });

      // local-vendor-vehicle-image / hotel-shuttle-image
      if (fetchSegmentImages) {
        try {
          const images = await fetchSegmentImages(activity);
          setDetail((s) => ({
            ...s,
            images: images ?? [],
            imagesLoading: false,
          }));
        } catch (e) {
          setDetail((s) => ({ ...s, images: [], imagesLoading: false }));
        }
      } else {
        setDetail((s) => ({ ...s, images: [], imagesLoading: false }));
      }

      if (kind === "transport" && fetchTripDriver) {
        try {
          const res = camelKeys(
            await fetchTripDriver(activity, employeeNumber),
          );
          setDetail((s) => ({
            ...s,
            driver: res?.driverInfo ?? null,
            crews: res?.crews ?? [],
            eta: res?.eta ?? null,
            tripLoading: false,
          }));
        } catch (e) {
          setDetail((s) => ({
            ...s,
            driver: null,
            crews: [],
            tripLoading: false,
          }));
        }
      } else if (kind === "flight" && fetchLegCrews) {
        // leg-crews
        try {
          const crews = camelKeys(await fetchLegCrews(activity));
          setDetail((s) => ({ ...s, crews: crews ?? [], tripLoading: false }));
        } catch (e) {
          setDetail((s) => ({ ...s, crews: [], tripLoading: false }));
        }
      } else if (kind === "hotel" && fetchHotelCrews) {
        // hotel-crews
        try {
          const crews = camelKeys(await fetchHotelCrews(activity));
          setDetail((s) => ({ ...s, crews: crews ?? [], tripLoading: false }));
        } catch (e) {
          setDetail((s) => ({ ...s, crews: [], tripLoading: false }));
        }
      } else if (kind === "hotelTransport" && fetchHotelTransportCrews) {
        try {
          const crews = camelKeys(await fetchHotelTransportCrews(activity));
          setDetail((s) => ({ ...s, crews: crews ?? [], tripLoading: false }));
        } catch (e) {
          setDetail((s) => ({ ...s, crews: [], tripLoading: false }));
        }
      } else {
        setDetail((s) => ({ ...s, crews: [], tripLoading: false }));
      }

      // hotel-amenities
      if (kind === "hotel") {
        if (fetchHotelAmenities) {
          try {
            const amenities = await fetchHotelAmenities(activity);
            setDetail((s) => ({
              ...s,
              amenities: amenities ?? [],
              amenitiesLoading: false,
            }));
          } catch (e) {
            setDetail((s) => ({
              ...s,
              amenities: [],
              amenitiesLoading: false,
            }));
          }
        } else {
          setDetail((s) => ({ ...s, amenities: [], amenitiesLoading: false }));
        }
      }

      if (kind === "transport" && fetchTripSecurityGuard) {
        try {
          const guard = camelKeys(
            await fetchTripSecurityGuard(activity, employeeNumber),
          );
          setDetail((s) => ({
            ...s,
            guard: guard ?? null,
            guardLoading: false,
          }));
        } catch (e) {
          setDetail((s) => ({ ...s, guard: null, guardLoading: false }));
        }
      }
    },
    [
      fetchSegmentImages,
      fetchTripDriver,
      fetchTripSecurityGuard,
      fetchHotelTransportCrews,
      fetchHotelCrews,
      fetchHotelAmenities,
      fetchLegCrews,
      employeeNumber,
    ],
  );

  /** Re-fetches get-trip-driver on demand (refresh icon), without re-triggering the full detail skeleton. */
  const refreshTripDriver = useCallback(
    async (activity) => {
      if (!fetchTripDriver) return;
      setDetail((s) => ({ ...s, driverRefreshing: true }));
      try {
        const res = camelKeys(await fetchTripDriver(activity, employeeNumber));
        setDetail((s) => ({
          ...s,
          driver: res?.driverInfo ?? null,
          crews: res?.crews ?? [],
          eta: res?.eta ?? null,
          driverRefreshing: false,
        }));
      } catch (e) {
        setDetail((s) => ({ ...s, driverRefreshing: false }));
      }
    },
    [fetchTripDriver, employeeNumber],
  );

  /**
   * Fetches get-trip-tracking (Track button open / Refresh pill / off-route auto-replot — see
   * the GPS-polling effect in TransportDetail, which calls this as connectTrip's `getTracking`).
   * `tracking.eta` now carries the single current-leg ETA/ETD message itself, so no separate
   * get-trip-driver-eta batch is needed.
   */
  const loadTripTracking = useCallback(
    async (activity) => {
      if (!fetchTripTracking) return null;
      setDetail((s) => ({ ...s, trackingLoading: true }));
      try {
        const trackingRes = camelKeys(
          await fetchTripTracking(activity, employeeNumber),
        );
        setDetail((s) => ({
          ...s,
          tracking: trackingRes ?? null,
          trackingLoading: false,
        }));
        return trackingRes ?? null;
      } catch (e) {
        setDetail((s) => ({
          ...s,
          tracking: null,
          trackingLoading: false,
        }));
        return null;
      }
    },
    [fetchTripTracking, employeeNumber],
  );

  const toggle = (key, activity) => {
    if (openKey === key) {
      setOpenKey(null);
      return;
    }
    setOpenKey(key);
    loadDetail(activity);
  };

  const handlers = {
    onCancelRide,
    onReadyForPickup,
    onEarlyPickup,
    onChangePickupAddress,
    onChangeDropoffAddress,
    onTrackVehicle,
    onCancelHotel,
    onChangeHotel,
    onChangeHomeAddress,
    onFetchAllocateHotelList,
    onFetchEarlyPickupTimes,
    onFetchStationAddress,
    onRefreshTripDriver: refreshTripDriver,
    onLoadTripTracking: loadTripTracking,
    onFetchVehicleGpsLocation: fetchVehicleGpsLocation
      ? (activity) => fetchVehicleGpsLocation(activity, employeeNumber)
      : undefined,
  };

  useEffect(() => {
    setOpenKey(null);
  }, [dashboardData]);

  const [allStations, setAllStations] = useState(false);
  const [crewList, setCrewList] = useState(null);
  const [staffOpen, setStaffOpen] = useState(false);
  const [staffSearch, setStaffSearch] = useState("");
  const headerRef = useRef(null);

  // keep the sticky day strip parked under the sticky header
  useEffect(() => {
    const measure = () => {
      if (headerRef.current) {
        document.documentElement.style.setProperty(
          "--ca-hdr-h",
          `${headerRef.current.offsetHeight}px`,
        );
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // GET /Crew/get-crew-dropdown-active?AirlineCode=<session airlineCode>
  useEffect(() => {
    if (!fetchCrewDropdown) return;
    let alive = true;
    Promise.resolve(fetchCrewDropdown())
      .then((list) => alive && setCrewList(Array.isArray(list) ? list : []))
      .catch(() => alive && setCrewList([]));
    return () => {
      alive = false;
    };
  }, [fetchCrewDropdown]);

  const selectedStaffName = useMemo(() => {
    const match = (crewList || []).find(
      (c) => String(c.employeeNumber) === String(employeeNumber),
    );
    return match?.employeeName || "";
  }, [crewList, employeeNumber]);
  const staffOptions = useMemo(() => {
    const q = (staffSearch || "").trim().toLowerCase();
    return (crewList || []).filter(
      (c) =>
        !q ||
        String(c.employeeName || "")
          .toLowerCase()
          .includes(q) ||
        String(c.employeeNumber || "").includes(q),
    );
  }, [crewList, staffSearch]);
  const stations = useMemo(() => {
    const out = [];
    (days || []).forEach((g) =>
      (g.activities || []).forEach((a) => {
        if (a.airportCode && out.indexOf(a.airportCode) === -1)
          out.push(a.airportCode);
      }),
    );
    return out;
  }, [days]);

  // Only one activity is ever live at a time; if more than one carries
  // isLiveTracking, prefer the one on the day with the highest sequenceNumber.
  const liveTrackingActivity = useMemo(() => {
    const daysDesc = [...(days || [])].sort(
      (a, b) => (b.sequenceNumber ?? 0) - (a.sequenceNumber ?? 0),
    );
    for (const day of daysDesc) {
      const match = (day.activities || []).find(
        (a) => a.isLiveTracking === true,
      );
      if (match) return { ...match, activityDateDisplay: day.activityDateDisplay };
    }
    return null;
  }, [days]);

  return (
    <div style={S.page}>
      <style>
        {"@keyframes skpulse { 0% { opacity: 1 } 50% { opacity: .45 } 100% { opacity: 1 } }" +
          "@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }" +
          `@keyframes liveTrackPulse { 0% { border-color: ${GREEN_DARK}; box-shadow: 0 0 6px 2px rgba(15,81,50,0.45); } 100% { border-color: ${GREEN_RADIANT}; box-shadow: 0 0 20px 6px rgba(34,197,94,0.9); } }` +
          ".ca-segment-card { border: 1px solid transparent; }" +
          `.ca-segment-card.is-live-tracking { border: 2px solid ${GREEN_DARK}; animation: liveTrackPulse 1.2s ease-in-out infinite alternate; }` +
          `@media (prefers-reduced-motion: reduce) { .ca-segment-card.is-live-tracking { animation: none; border: 2px solid ${GREEN_DARK}; box-shadow: 0 0 0 4px rgba(34,197,94,0.45); } }` +
          ".ca-driver-header { flex-wrap: wrap; }" +
          ".ca-driver-actions { flex: 1 1 100%; justify-content: flex-start; }" +
          "@media (min-width: 640px) { .ca-driver-header { flex-wrap: nowrap; } .ca-driver-actions { flex: 0 1 auto; justify-content: flex-end; } }" +
          "@keyframes caMarquee { from { left: 100%; } to { left: -100%; } }" +
          ".ca-tracking-banner { position: relative; }" +
          ".ca-tracking-marquee-item { position: absolute; top: 0; bottom: 0; left: 100%; display: inline-flex; align-items: center; gap: 9px; white-space: nowrap; animation: caMarquee 26s linear infinite; }" +
          ".ca-tracking-marquee-item.ca-tracking-marquee-item--b { animation-delay: -13s; }" +
          ".ca-tracking-banner:hover .ca-tracking-marquee-item { animation-play-state: paused; }" +
          "@keyframes pulseDot { 0% { opacity: 1 } 50% { opacity: .35 } 100% { opacity: 1 } }" +
          ".ca-route-flow { stroke-dasharray: 10 26; animation: caRouteFlow 1.1s linear infinite; }" +
          "@keyframes caRouteFlow { to { stroke-dashoffset: -36; } }" +
          // Dark map theme for the Track button map — same treatment as TripTrackingMap /
          // LiveTripMap: invert+hue-rotate the OSM tiles to a dark look, everything drawn on top
          // (markers, polylines, popups) is untouched so the purple/orange marker colors keep
          // showing through as-is.
          ".ca-track-map-dark .leaflet-tile-pane { filter: invert(1) hue-rotate(185deg) brightness(.92) contrast(.86) saturate(.55); }" +
          ".ca-track-map-dark.leaflet-container, .ca-track-map-dark .leaflet-container { background: #0c1014; }" +
          ".ca-track-map-dark .leaflet-control-zoom a { background: rgba(14,19,25,.87); color: #e9eef3; border-color: rgba(255,255,255,.11); }" +
          ".ca-track-map-dark .leaflet-control-zoom a:hover { background: #1b232c; color: #22C55E; }" +
          ".ca-track-map-dark .leaflet-control-attribution { background: rgba(12,16,20,.8) !important; color: #69757f; font-size: 9.5px; }" +
          ".ca-track-map-dark .leaflet-control-attribution a { color: #8e9aa6; }" +
          // Slide-in Live Trip Tracking panel — same look/animation as DispatchComponent's
          // .dc-live-panel and TrackingComponent's .tc-live-panel. The app shell's own top
          // navbar is fixed at the very top of the viewport and paints over anything else
          // sharing that space — starting this panel at top:0 put its header (title/close
          // button) directly underneath that navbar, hiding both, exactly like Dispatch's own
          // comment on its version describes. Offset below the navbar's height instead of
          // fighting it with z-index (same 50px Dispatch already uses for this same navbar).
          ".ca-live-panel { position: fixed; top: 50px; right: 0; bottom: 0; width: clamp(560px, 60vw, 1000px); max-width: 100vw; z-index: 2100; display: flex; flex-direction: column; background: #11161c; border-left: 1px solid rgba(255,255,255,0.08); box-shadow: -12px 0 40px rgba(0,0,0,0.45); animation: caLivePanelIn 0.28s ease; }" +
          "@keyframes caLivePanelIn { from { transform: translateX(100%); } to { transform: translateX(0); } }" +
          ".ca-live-panel-header { position: relative; display: flex; align-items: center; gap: 10px; padding: 14px 44px 14px 18px; background: linear-gradient(135deg, #16211c 0%, #1a2b22 60%, #163024 100%); border-bottom: 1px solid rgba(62,224,163,0.25); color: #eafff6; font-size: 15px; font-weight: 600; flex-shrink: 0; }" +
          ".ca-live-panel-close { position: absolute; top: 10px; right: 10px; width: 32px; height: 32px; border-radius: 8px; border: none; background: rgba(255,255,255,0.12); color: #eafff6; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 14px; flex-shrink: 0; transition: background 0.15s ease, color 0.15s ease; }" +
          ".ca-live-panel-close:hover { background: rgba(255,90,90,0.18); color: #ff8a8a; }" +
          ".ca-live-panel-body { flex: 1; min-height: 0; overflow: hidden; }" +
          ".ca-live-panel-map { height: 100%; }" +
          "@media (max-width: 640px) { .ca-live-panel { width: 100vw; } }"}
      </style>

      <div style={S.header} ref={headerRef}>
        <img
          src="../assets/img/logo_akasa_air_1.png"
          alt="Akasa Air"
          style={{ height: 19, filter: "brightness(0) invert(1)" }}
        />
        <div style={S.headerRule} />
        <div>
          <div
            style={{
              fontSize: 15,
              fontWeight: 800,
              color: "#fff",
              lineHeight: 1.2,
            }}
          >
            Staff {employeeNumber}
            {selectedStaffName ? ` - ${selectedStaffName}` : ""}
          </div>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              color: "rgba(255,255,255,0.85)",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "0 5px",
            }}
          >
            {airlineCode ? `${airlineCode} \u00b7 ` : ""}
            <span
              title={stations.join(" / ")}
              onClick={() => stations.length > 3 && setAllStations((v) => !v)}
              style={{
                cursor: stations.length > 3 ? "pointer" : "default",
                textDecoration: "underline",
                textDecorationColor: "rgba(255,255,255,0.4)",
                textUnderlineOffset: 2,
              }}
            >
              {allStations || stations.length <= 3
                ? stations.join(" / ")
                : `${stations.slice(0, 3).join(" / ")} +${stations.length - 3}`}
            </span>
            {` \u00b7 ${total} ${total === 1 ? "record" : "records"}`}
          </div>
        </div>
        {fetchCrewDropdown && (
          <div
            style={{
              position: "relative",
              flex: "1 1 300px",
              minWidth: 220,
              maxWidth: 360,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 800,
                color: "rgba(255,255,255,0.85)",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                marginBottom: 3,
              }}
            >
              Staff
            </div>
            <div
              onClick={() => {
                setStaffOpen((v) => !v);
                setStaffSearch("");
              }}
              style={{
                height: 34,
                borderRadius: 8,
                background: "#fff",
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "0 10px",
                cursor: "pointer",
              }}
            >
              <i
                className="fas fa-user"
                style={{ color: GREY, fontSize: 11 }}
              />
              <span
                style={{
                  flex: "1 1 0",
                  minWidth: 0,
                  fontSize: 13,
                  fontWeight: 700,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: employeeNumber ? "#1c1c1e" : GREY,
                }}
              >
                {employeeNumber
                  ? `Staff ${employeeNumber}${selectedStaffName ? ` - ${selectedStaffName}` : ""}`
                  : "Select Staff"}
              </span>
              <i
                className={staffOpen ? "fas fa-caret-up" : "fas fa-caret-down"}
                style={{ color: PURPLE, fontSize: 13 }}
              />
            </div>

            {staffOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  marginTop: 5,
                  zIndex: 900,
                  background: "#fff",
                  borderRadius: 10,
                  boxShadow: "0 12px 32px rgba(30,20,60,0.24)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: "9px 10px",
                    borderBottom: "1px solid #f0eff5",
                  }}
                >
                  <div
                    style={{
                      height: 32,
                      border: "1px solid #dcd6ef",
                      borderRadius: 8,
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      padding: "0 10px",
                    }}
                  >
                    <i
                      className="fas fa-search"
                      style={{ color: GREY, fontSize: 11 }}
                    />
                    <input
                      type="text"
                      value={staffSearch}
                      onChange={(e) => setStaffSearch(e.target.value)}
                      placeholder="Search"
                      style={{
                        flex: "1 1 0",
                        minWidth: 0,
                        border: "none",
                        outline: "none",
                        fontFamily: "inherit",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#1c1c1e",
                        background: "transparent",
                      }}
                    />
                  </div>
                </div>

                {!crewList ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      padding: 10,
                    }}
                  >
                    <Skeleton height={34} />
                    <Skeleton height={34} />
                    <Skeleton height={34} />
                    <Skeleton height={34} />
                  </div>
                ) : staffOptions.length === 0 ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 16,
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: GREY,
                    }}
                  >
                    No staff found
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      maxHeight: 300,
                      overflowY: "auto",
                    }}
                  >
                    {staffOptions.map((c) => {
                      const picked =
                        String(c.employeeNumber) === String(employeeNumber);
                      return (
                        <div
                          key={c.employeeNumber}
                          onClick={() => {
                            setStaffOpen(false);
                            setStaffSearch("");
                            if (onSelectStaff) onSelectStaff(c);
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "9px 12px",
                            borderBottom: "1px solid #f5f4f9",
                            cursor: "pointer",
                            background: picked ? "#F7F5FE" : "#fff",
                          }}
                        >
                          <span
                            style={{
                              width: 16,
                              height: 16,
                              borderRadius: "50%",
                              border: `2px solid ${picked ? PURPLE : "#c4c2ce"}`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            {picked && (
                              <span
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  background: PURPLE,
                                }}
                              />
                            )}
                          </span>
                          <span style={{ minWidth: 0 }}>
                            <span
                              style={{
                                display: "block",
                                fontSize: 13,
                                fontWeight: 700,
                                color: "#1c1c1e",
                              }}
                            >
                              {c.employeeName}
                            </span>
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 5,
                                fontSize: 11,
                                fontWeight: 600,
                                color: GREY,
                              }}
                            >
                              <i
                                className="fas fa-user"
                                style={{ fontSize: 9 }}
                              />
                              {c.employeeNumber}
                            </span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <button type="button" style={S.backLink} onClick={onBack}>
            <i className="fas fa-arrow-left" style={{ fontSize: 10 }} />
            Back
          </button>
          <button
            type="button"
            title="Refresh"
            style={S.iconButton}
            onClick={onRefresh}
          >
            <i
              className={`fas fa-${isLoading ? "spinner fa-spin" : "sync-alt"}`}
              style={{ fontSize: 13 }}
            />
          </button>
        </div>
      </div>

      {liveTrackingActivity && (
        <div
          style={{
            position: "sticky",
            top: "var(--ca-hdr-h, 68px)",
            zIndex: 900,
            pointerEvents: "none",
          }}
        >
          <div
            className="ca-tracking-banner"
            style={{
              height: 26,
              width: "100%",
              padding: "0 16px",
              borderRadius: 6,
              background: GREEN_DARK,
              color: "#fff",
              boxShadow: `0 4px 16px rgba(15,81,50,0.35)`,
              fontSize: 12.5,
              fontWeight: 700,
              overflow: "hidden",
              pointerEvents: "auto",
              boxSizing: "border-box",
            }}
          >
            {["", " ca-tracking-marquee-item--b"].map((variant) => (
              <span
                className={`ca-tracking-marquee-item${variant}`}
                key={variant}
              >
                <i
                  className="fas fa-car-side"
                  style={{ color: GREEN_RADIANT, fontSize: 13 }}
                />
                <span>
                  Tracking live for {show(liveTrackingActivity.airportCode)}{" "}
                  - {show(liveTrackingActivity.activityDateDisplay)} -
                </span>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                  }}
                >
                  <i className="far fa-clock" style={{ fontSize: 10 }} />
                  <span>
                    {show(
                      liveTrackingActivity.dashboardActivityLocalTimeValue,
                    )}
                  </span>
                </span>
                {liveTrackingActivity.trackingStatus && (
                  <span>
                    <Dot /> {liveTrackingActivity.trackingStatus}
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      <div style={{ paddingBottom: 28 }}>
        {isLoading && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              padding: "12px 0 0",
            }}
          >
            {[0, 1, 2].map((n) => (
              <div
                key={n}
                style={{ display: "flex", flexDirection: "column", gap: 8 }}
              >
                <div
                  style={{
                    background: "#DDD6FE",
                    height: 52,
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    paddingRight: 16,
                  }}
                >
                  <span
                    style={{
                      width: 44,
                      height: 52,
                      background: PURPLE,
                      borderRadius: "0 10px 10px 0",
                    }}
                  />
                  <Skeleton height={12} width={120} />
                  <span style={{ marginLeft: "auto" }}>
                    <Skeleton height={22} width={92} />
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    padding: "0 16px",
                  }}
                >
                  <Skeleton height={64} />
                  <Skeleton height={64} />
                </div>
              </div>
            ))}
          </div>
        )}

        {!isLoading && days.length === 0 && (
          <div
            style={{
              textAlign: "center",
              fontSize: 14,
              color: GREY,
              fontWeight: 600,
              padding: "40px 0",
            }}
          >
            No activities for the selected period
          </div>
        )}

        {!isLoading &&
          days.map((day, di) => {
            const activities = day.activities ?? [];
            return (
              <div
                key={`day-${di}-${day.sequenceNumber ?? day.activityDateDisplay ?? ""}`}
              >
                <div style={S.dayStrip}>
                  <div style={S.dayChip}>
                    <span style={{ fontSize: 10, fontWeight: 700 }}>
                      {day.activityMonthName}
                    </span>
                    <span style={{ fontSize: 19, fontWeight: 800 }}>
                      {day.activityDayNumber}
                    </span>
                    <span style={{ fontSize: 10, fontWeight: 700 }}>
                      {day.activityDayName}
                    </span>
                  </div>
                  <span style={S.dayCount}>
                    {activities.length}{" "}
                    {activities.length === 1 ? "Segment" : "Segments"}
                  </span>
                </div>
                <div style={S.list}>
                  {activities.map((activity, i) => {
                    const key = `${di}-${i}`;
                    return (
                      <SegmentCard
                        key={`seg-${di}-${i}`}
                        activity={activity}
                        expanded={openKey === key}
                        onToggle={() => toggle(key, activity)}
                        detailState={detail}
                        handlers={handlers}
                        employeeNumber={employeeNumber}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default CrewActivityMobileViewComponent;
